import { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import CircularProgress from "./CircularProgress";
import { GetImages } from "../../utils/GetImages";
import "./FileUploader.css";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import SvgRoundIconButton from "../buttons/Svg-Round-Icon-button";

interface PDFUploaderProps {
  name: string;
  uploaderId: string;
  label: string;
  uploadertext?: string;
  required?: boolean;
  disabled?: boolean;
  requiredMessage?: string;
  maxSizeMB?: number;
  maxFiles?: number;
  resetTrigger?: number;
  uploadertypeslabel?: string;
  defaultFiles?: Array<{ src: string; name: string }>;
  allowMultiple?: boolean;
}

const PDFUploader = ({
  name,
  uploaderId,
  label,
  required = false,
  disabled = false,
  requiredMessage = "Please upload a PDF file",
  maxSizeMB = 5,
  maxFiles = 5,
  resetTrigger = 0,
  uploadertext = "Upload PDF(s)",
  uploadertypeslabel = `Max. size ${maxSizeMB}MB. PDF only (Max ${maxFiles} files)`,
  defaultFiles = [],
  allowMultiple = true,
}: PDFUploaderProps) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const [filesMap, setFilesMap] = useState<Record<string, { file: File; name: string }[]>>({});
  const files = filesMap[name] || [];
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Initialize with defaultFiles
  useEffect(() => {
    if (!defaultFiles.length || initializedRef.current) return;
    initializedRef.current = true;

    const processDefaultFiles = async () => {
      const processedFiles: { file: File; name: string }[] = [];
      for (let i = 0; i < defaultFiles.length && i < maxFiles; i++) {
        const defaultFile = defaultFiles[i];
        try {
          const response = await fetch(defaultFile.src);
          const blob = await response.blob();
          const file = new File([blob], defaultFile.name, { type: "application/pdf" });
          processedFiles.push({ file, name: defaultFile.name });
        } catch (error) {
          console.error("Error processing default file:", error);
        }
      }
      if (processedFiles.length > 0) {
        setFilesMap((prev) => ({ ...prev, [name]: processedFiles }));
        setValue(name, processedFiles.map((item) => item.file), { shouldValidate: true });
      }
    };

    processDefaultFiles();
  }, [defaultFiles, name, setValue, maxFiles]);

  // Reset on trigger
  useEffect(() => {
    if (resetTrigger === 0) return;
    setFilesMap((prev) => ({ ...prev, [name]: [] }));
    setValue(name, [], { shouldValidate: true });
    clearErrors(name);
    initializedRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [resetTrigger, setValue, clearErrors, name]);

  const clearProgress = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return prev;
        }
        return prev + 10;
      });
    }, 100);
  };

  const validateFile = (file: File) => {
    if (file.type !== "application/pdf") {
      return "Only PDF files are allowed";
    }
    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > maxSizeMB) {
      return `Max file size is ${maxSizeMB}MB`;
    }
    return true;
  };

  const handleFileChange = async (newFiles: FileList | null, fieldOnChange: (value: File[] | null) => void) => {
    if (!newFiles || newFiles.length === 0) return;

    clearErrors(name);

    if (files.length + newFiles.length > maxFiles) {
      setError(name, {
        type: "manual",
        message: `You can upload maximum ${maxFiles} files`,
      });
      return;
    }

    const validFiles: { file: File; name: string }[] = [];
    let validationError = "";

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];
      const result = validateFile(file);
      if (result !== true) {
        validationError = result;
        break;
      }
      validFiles.push({ file, name: file.name });
    }

    if (validationError) {
      setError(name, {
        type: "manual",
        message: validationError,
      });
      return;
    }

    if (validFiles.length === 0) return;

    setLoading(true);
    clearProgress();

    setTimeout(() => {
      setLoading(false);
      const updatedFiles = [...files, ...validFiles];
      setFilesMap((prev) => ({ ...prev, [name]: updatedFiles }));
      const fileArray = updatedFiles.map((item) => item.file);
      setValue(name, fileArray, { shouldValidate: true });
      fieldOnChange(fileArray);
    }, 1500);
  };

  const handleDelete = (index: number, onChange: (value: File[] | null) => void) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFilesMap((prev) => ({ ...prev, [name]: updatedFiles }));
    const fileArray = updatedFiles.length > 0 ? updatedFiles.map((item) => item.file) : [];
    setValue(name, fileArray, { shouldValidate: true });
    onChange(fileArray);
    clearErrors(name);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (files: File[] | null) => {
          if (required && (!files || files.length === 0)) return requiredMessage;
          if (files) {
            for (const file of files) {
              const result = validateFile(file);
              if (result !== true) return result;
            }
          }
          return true;
        },
      }}
      render={({ field: { onChange } }) => (
        <div className="form-group">
          <label className="fw-600">
            {label}{" "}
            {required ? (
              <span className="text-danger">*</span>
            ) : (
              <span className="optional-text-color">(Optional)</span>
            )}
          </label>
          <div className="uploaderContainer">
            <div className={`pdf-uploader-box-container border-image-dashed ${loading ? "uploading-bg-color" : ""}`}>
              <input
                disabled={disabled}
                type="file"
                ref={fileInputRef}
                id={`upload_${uploaderId}`}
                className="upload-input pdf-upload-input"
                onChange={(e) => handleFileChange(e.target.files, onChange)}
                accept="application/pdf"
                multiple={allowMultiple}
              />
              <label htmlFor={`upload_${uploaderId}`} className="upload-label">
                <div className="emptyImage gap-3">
                  {loading ? (
                    <div className="d-flex flex-column align-items-center gap-2">
                      <CircularProgress
                        size={40}
                        thickness={3}
                        isIcon={false}
                        color="blue"
                        BG_transparent={true}
                        progress={progress}
                      />
                    </div>
                  ) : (
                    <img className="image-icon-size-30" src={GetImages.pdfImage} alt="pdf-upload" />
                  )}
                  <div className="sub_text fw-700 black font-size-16">{uploadertext}</div>
                  <p className="sub_text font-size-15 grey_subtext1 mt-1">{uploadertypeslabel}</p>
                </div>
              </label>
            </div>

            <div className="row mt-2">
              {files.map((item, index) => (
                <div key={index} className="col-md-4 mb-2">
                  <div className="preview-container position-relative">
                    <div className="file-preview d-flex align-items-center">
                      <img
                        src={GetImages.pdfImage}
                        alt={`pdf-icon-${index}`}
                        className="file-icon-size-50 me-2"
                      />
                      <span className="file-name-font-size text-truncate" title={item.name}>
                        {item.name}
                      </span>
                    </div>
                    <SvgRoundIconButton
                      style={{ position: "absolute", top: -10, right: -10 }}
                      onClick={() => handleDelete(index, onChange)}
                      Icon={<DeleteRedIcon fill="#fff" width="22" height="22" />}
                    />
                  </div>
                </div>
              ))}
            </div>

            {errors[name] && (
              <div className="invalid-feedback d-block">{errors[name]?.message as string}</div>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default PDFUploader;